import {
  Text,
  Box,
  VStack,
  Image,
  Skeleton,
  Flex,
  Spinner,
  HStack,
  Input,
  Button,
} from "@chakra-ui/react";
import { ChangeEvent, useState } from "react";
import { useAppDispatch } from "../../../Store/hooks";

import SwiperCore, { Navigation, A11y } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/swiper.scss";
import "swiper/components/navigation/navigation.scss";
import Loader from "../../Helpers/Loader";
import { gql, useQuery } from "@apollo/client";
import { DoubleSide, LoadingManager, Group, Vector3, Box3 } from "three";
import { MTLLoader } from "three/examples/jsm/loaders/MTLLoader";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader";

import { colors } from "../../../Theme";
import Checkbox from "../../Helpers/Checkbox";
import { ModalsName, setModalOpen } from "../../../Store/ModalsReducer";

SwiperCore.use([Navigation, A11y]);

const LOAD_ELEMENTS = gql`
  query LoadElements {
    loadAll(type: "element", forMaster: true) {
      items {
        id
        name
        preview
        path3D {
          dirname
          obj
          mtl
        }
      }
      total
    }
  }
`;

export type ItemInfo = {
  id: number;
  name: string;
  preview: string;
  path3D: {
    dirname: string;
    obj: string;
    mtl: string;
  };
};

const CoveringMaster = (props: {
  createCovering: (args: {
    x: number;
    model: ItemInfo;
    z: number;
    fill: boolean;
    name: string;
  }) => Promise<string>;
}) => {
  const dispatch = useAppDispatch();

  const [_x, _setX] = useState(1);
  const [_z, _setZ] = useState(1);

  const [_fill, _setFill] = useState(true);
  const [_name, _setName] = useState("");

  const [_loading, _setLoading] = useState(false);

  const { data, loading } = useQuery(LOAD_ELEMENTS);

  const [sizes, setSizes] = useState<null | Vector3>(null);

  const [_id, _setId] = useState(0);

  if (loading) return <Spinner sizes="lg" />;

  const loadMtlAsync = (
    url: string
  ): Promise<[MTLLoader.MaterialCreator, boolean]> => {
    return new Promise((resolve, reject) => {
      let managerMTL = new LoadingManager();
      let mtlLoader = new MTLLoader(managerMTL);
      mtlLoader.setMaterialOptions({
        side: DoubleSide,
      });
      let fromSketchUp = false;
      mtlLoader.load(
        url,
        (mats: MTLLoader.MaterialCreator) => resolve([mats, fromSketchUp]),
        (ev: ProgressEvent<EventTarget>) => {
          if ((ev.target as XMLHttpRequest).response.indexOf("SketchUp") !== -1)
            fromSketchUp = true;
        },
        (err: any) => {
          console.log(err);
          reject(err);
        }
      );
    });
  };

  const loadObjAsync = (
    url: string,
    materials: MTLLoader.MaterialCreator
  ): Promise<Group> => {
    return new Promise((resolve, reject) => {
      let managerOBJ = new LoadingManager();
      let objLoader = new OBJLoader(managerOBJ);
      try {
        objLoader.setMaterials(materials);
      } catch (ex) {
        console.log(ex);
      }
      objLoader.load(url, resolve, undefined, reject);
    });
  };

  const getSizes = async (id: number) => {
    if (!data) throw new Error("No data loaded");
    const item = data.loadAll.items.find((item: ItemInfo) => item.id === id);
    if (!item) throw new Error("Item not found");
    const [materials] = await loadMtlAsync(
      `${item.path3D.dirname}/${item.path3D.mtl}`
    );
    materials.preload();
    const node = await loadObjAsync(
      `${item.path3D.dirname}/${item.path3D.obj}`,
      materials
    );
    const sizesObj = new Vector3();
    new Box3().setFromObject(node).getSize(sizesObj);
    setSizes(sizesObj);
    _setX(Math.round(sizesObj.x));
    _setZ(Math.round(sizesObj.z));
    _setId(id);
  };

  const handleSave = async () => {
    if (!sizes) throw new Error("No sizes");
    if (!data) throw new Error("No data");
    _setLoading(true);
    await props.createCovering({
      x: Math.round(_x / sizes.x),
      model: data.loadAll.items.find((item: ItemInfo) => item.id === _id),
      z: Math.round(_z / sizes.z),
      fill: _fill,
      name: _name.length > 0 ? _name : "Новое покрытие",
    });
    _setLoading(false);
    dispatch(setModalOpen({ key: ModalsName.ADD, value: false }));
  };

  let count = 1;
  if (sizes) {
    const x = _x / Math.round(sizes.x);
    const z = _z / Math.round(sizes.z);
    if (_fill) {
      count = x * z;
    } else {
      count = 2 * x + 2 * z - 4;
    }
  }

  return (
    <Box>
      <VStack>
        <Text fontWeight="bold">Выберите элемент</Text>
        <Box w="100%">
          <Swiper navigation slidesPerView={7}>
            {data.loadAll.items.map((item: ItemInfo) => (
              <SwiperSlide key={`item${item.id}`}>
                <Item {...item} getSizes={() => getSizes(item.id)} />
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
        {sizes && (
          <VStack align="center">
            <Text fontWeight="bold">Укажите размер покрытия</Text>
            <HStack>
              <VStack>
                <Text fontWeight="bold">Длина</Text>
                <HStack>
                  <Text fontSize="2rem" color="red">
                    X:
                  </Text>
                  <Input
                    type="number"
                    textAlign="center"
                    step={sizes.x}
                    value={_x}
                    borderColor={colors.brown}
                    borderRadius={0}
                    fontSize="2rem"
                    onChange={(ev: ChangeEvent<HTMLInputElement>) =>
                      _setX(parseInt(ev.target.value, 10))
                    }
                  />
                </HStack>
              </VStack>
              <VStack>
                <Text fontWeight="bold">Ширина</Text>
                <HStack>
                  <Text fontSize="2rem" color="blue">
                    Z:
                  </Text>
                  <Input
                    textAlign="center"
                    type="number"
                    step={sizes.z}
                    value={_z}
                    borderColor={colors.brown}
                    borderRadius={0}
                    fontSize="2rem"
                    onChange={(ev: ChangeEvent<HTMLInputElement>) =>
                      _setZ(parseInt(ev.target.value, 10))
                    }
                  />
                </HStack>
              </VStack>
            </HStack>
            <VStack>
              <Text fontWeight="bold">Количество</Text>
              <Input
                textAlign="center"
                type="number"
                value={count}
                borderColor={colors.brown}
                borderRadius={0}
                fontSize="2rem"
              />
            </VStack>
            <HStack>
              <Checkbox
                checked={_fill}
                onChange={(ev: ChangeEvent<HTMLInputElement>) =>
                  _setFill(ev.target.checked)
                }
              />
              <Text fontWeight="bold">Заполнить</Text>
            </HStack>
            <Input
              textAlign="center"
              value={_name}
              borderColor={colors.brown}
              borderRadius={0}
              fontSize="2rem"
              placeholder="Название покрытия"
              onChange={(ev: ChangeEvent<HTMLInputElement>) =>
                _setName(ev.target.value)
              }
            />
          </VStack>
        )}
        <Box marginTop="2rem">
          <Button variant="editor" fontSize="2rem" onClick={handleSave} isLoading={_loading}>
            Создать
          </Button>
        </Box>
      </VStack>
    </Box>
  );
};

const Item = (props: ItemInfo & { getSizes: () => void }) => {
  const loading = false;
  return (
    <Box
      w={200}
      h={175}
      textAlign="center"
      border="#333 1px solid"
      cursor="pointer"
      onClick={props.getSizes}
    >
      {loading ? (
        <Flex h={200}>
          <Loader />
        </Flex>
      ) : (
        <>
          <Text w={198} h={25} bg="#333" color="white" fontWeight="bold">
            {props.name}
          </Text>
          <Box w={198} h={148} align="center">
            <Image
              src={props.preview}
              alt={props.preview}
              w={198}
              h={148}
              fallback={<Skeleton w={200} h={150} />}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default CoveringMaster;
